import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { FC, MouseEvent, useCallback, useMemo } from 'react'
import { UPDATE_PREFERRED_PAYMENT_METHOD_MUTATION_KEY, useUpdatePreferredPaymentMethod } from 'dataQueries'

import { MUIBadge } from '../MUIBadge'
import { MUIButton } from '../MUIButton'
import { PreferredPaymentMethodEnum } from 'constants/user'
import { PreferredPaymentMethodType } from 'models/user'
import styles from './PreferredPaymentMethodSwitch.module.sass'
import { useIsMutating, useMutationState } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

interface Props {
  paymentMethod: PreferredPaymentMethodType
  /** Whether user me is directly updated on value change (default true) */
  directlyUpdateUserMe?: boolean
}

export const PreferredPaymentMethodSwitch: FC<Props> = ({
  paymentMethod,
  directlyUpdateUserMe = true,
}) => {
  const { t } = useTranslation(['preferred_payment_method'])

  const { clientData, hasWorkspaceActiveSubscription } = useUserData()
  const updateMethod = useUpdatePreferredPaymentMethod()

  // Every payment method renders its own switch instance, so the badge must follow the latest
  // successful save across ALL instances (via the shared mutation key) — reading this instance's
  // own mutation state left a stale badge on every method that was saved once while mounted.
  const lastSavedMethod = useMutationState({
    filters: { mutationKey: UPDATE_PREFERRED_PAYMENT_METHOD_MUTATION_KEY, exact: true, status: 'success' },
    select: (mutation) => (mutation.state.variables as { paymentMethod: PreferredPaymentMethodType } | undefined)?.paymentMethod,
  }).at(-1)

  // Saves race across instances (.at(-1) is submission order, not resolution order), so block
  // every save button while any instance's save is in flight.
  const isAnySavePending = useIsMutating({ mutationKey: UPDATE_PREFERRED_PAYMENT_METHOD_MUTATION_KEY, exact: true }) > 0

  const initialPreferredMethod = useMemo(() => clientData?.preferredPaymentMethod || PreferredPaymentMethodEnum.NONE, [clientData])
  const activePreferredMethod = useMemo(() => lastSavedMethod || initialPreferredMethod, [lastSavedMethod, initialPreferredMethod])
  const isThisMethodBeingSet = useMemo(() => updateMethod.variables?.paymentMethod === paymentMethod, [updateMethod.variables?.paymentMethod, paymentMethod])

  const onSaveAsDefaultPayment = useCallback((e: MouseEvent) => {
    e.stopPropagation()

    // Mutation first: analytics is best-effort and must never be able to swallow the click (ISSUE-3098).
    updateMethod.mutate({ paymentMethod, callUpdateUserMe: directlyUpdateUserMe })

    logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_PAYMENT_METHOD_SAVED, {
      PaymentMethodSelected: paymentMethod,
      IsSubscribed: hasWorkspaceActiveSubscription
    })
  }, [directlyUpdateUserMe, hasWorkspaceActiveSubscription, paymentMethod, updateMethod])

  return (
    <div className={styles.preferredPaymentMethodSwitch}>

      {(activePreferredMethod !== paymentMethod) &&
        <MUIButton
          className={styles.saveButton}
          disabled={isAnySavePending}
          onClick={e => onSaveAsDefaultPayment(e)}
          type="secondaryBorder"
          isLoading={isThisMethodBeingSet && updateMethod.isPending}
        >
          {t('save')}
        </MUIButton>
      }

      {isThisMethodBeingSet && updateMethod.isError &&
        <span className={styles.saveError}>
          {t('error')}
        </span>
      }

      {activePreferredMethod === paymentMethod &&
        <MUIBadge color="blue" variant="filled" label={t('badge')} />
      }

    </div>
  )
}
